import React from 'react';

export function Sparkline({ data = [], width = 120, height = 36, positive = true }) {
  const id = React.useId();
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  const color = positive ? '#39FF14' : '#FF4545';
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <polygon points={`0,${height} ${pts} ${width},${height}`} fill={`url(#grad-${id})`} />
    </svg>
  );
}

export function Candlestick({ candles = [], height = 360 }) {
  if (!candles || candles.length === 0)
    return <div style={{ height }} className="grid place-items-center text-vj-grey text-sm">No chart data</div>;

  const data = candles.slice(-80);
  const width = 900;
  const padX = 8;
  const padBottom = 24;
  const padTop = 12;
  const highs = data.map((c) => c.h);
  const lows = data.map((c) => c.l);
  const max = Math.max(...highs);
  const min = Math.min(...lows);
  const range = max - min || 1;
  const chartH = height - padBottom - padTop;
  const slot = (width - padX * 2) / data.length;
  const bw = Math.max(2, slot * 0.6);
  const y = (v) => padTop + chartH - ((v - min) / range) * chartH;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const val = max - f * range;
        const yy = padTop + f * chartH;
        return (
          <g key={i}>
            <line x1={padX} x2={width - padX} y1={yy} y2={yy} stroke="rgba(138,148,141,0.14)" strokeWidth="1" />
            <text x={width - padX} y={yy - 3} fontSize="10" fill="#8A948D" textAnchor="end" fontFamily="JetBrains Mono">
              {val.toLocaleString(undefined, { maximumFractionDigits: val > 100 ? 0 : 4 })}
            </text>
          </g>
        );
      })}
      {data.map((c, i) => {
        const up = c.c >= c.o;
        const color = up ? '#00C853' : '#FF4545';
        const cx = padX + i * slot + slot / 2;
        const openY = y(c.o);
        const closeY = y(c.c);
        const top = Math.min(openY, closeY);
        const bodyH = Math.max(1, Math.abs(closeY - openY));
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={y(c.h)} y2={y(c.l)} stroke={color} strokeWidth="1.2" />
            <rect x={cx - bw / 2} y={top} width={bw} height={bodyH} fill={color} rx="0.5" />
          </g>
        );
      })}
    </svg>
  );
}
